import get from 'lodash/get'
import * as React from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { useParams } from 'react-router-dom'
import type { EmailCampaign } from 'types/campaign'
import type { State } from 'types/redux'
import { campaignListDuplicate } from '@disco/state-management/actions/campaign-list-actions'
import { loadCampaignStats } from '@disco/state-management/actions/campaign-stats-actions'
import mixpanel from '@disco/api/mixpanel'
import { EMAIL_CAMPAIGN_BUILDER } from '@disco/constants/FeatureTypes'
import { makeIsFeatureEnabled } from '@disco/state-management/selectors/business'
import { getCampaign } from '@disco/state-management/selectors/campaigns'
import DocumentHeadTags from 'components/document-head-tags'
import { Spinner } from 'components/ui'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import PageHeader from 'components/ui/page-header'
import CampaignDashboard from 'pages/campaigns/campaign-stats/campaign-dashboard'
import type { RootState } from 'store/configureStore'
import styles from './index.module.css'

interface AdditionalProps {
  children: React.ReactNode
  params: {
    campaignId: string
  }
}

type Props = PropsFromRedux & AdditionalProps
type OwnState = {
  duplicatingBrief: boolean
  loading: boolean
}

const mapStateToProps = (
  state: RootState,
  { params: { campaignId } }: { params: { campaignId: string } },
) => {
  const isFeatureEnabled = makeIsFeatureEnabled()
  return {
    campaign: getCampaign(state, {
      campaignId: parseInt(campaignId, 10),
    }),
    isFeatureEnabled: isFeatureEnabled(state, {
      featureName: EMAIL_CAMPAIGN_BUILDER,
    }),
  }
}

const mapDispatchToProps = {
  loadCampaignStats,
  campaignListDuplicate,
}

const connector = connect(mapStateToProps, mapDispatchToProps)

type PropsFromRedux = ConnectedProps<typeof connector>

class CampaignStatsPage extends React.Component<Props, OwnState> {
  state = {
    duplicatingBrief: false,
    loading: false,
  }
  componentDidMount = async () => {
    const { params, loadCampaignStats } = this.props
    this.setState({
      loading: true,
    })
    await loadCampaignStats(parseInt(params.campaignId, 10), false)
    this.setState({
      loading: false,
    })
  }
  _onDuplicateBrief = async () => {
    const { campaignListDuplicate, campaign } = this.props
    if (!campaign) return
    this.setState({
      duplicatingBrief: true,
    })
    await campaignListDuplicate(campaign.id)
    this.setState({
      duplicatingBrief: false,
    })
  }

  render() {
    const { campaign, children, params, isFeatureEnabled } = this.props
    const { duplicatingBrief, loading } = this.state
    if (!campaign) return null
    const campaignName = campaign.name || `Untitled ${campaign.type}`
    return loading ? (
      <Spinner />
    ) : (
      <div className="Page_Content">
        <DocumentHeadTags
          title={`${campaignName} report | DISCO`}
          link={{
            href: 'https://fonts.googleapis.com/css2?family=Roboto&display=swap',
            rel: 'stylesheet',
          }}
        />
        <PageHeader
          breadcrumbs={{
            content: 'Email creator',
            link: '/campaigns/list',
          }}
          title={campaignName}
          actions={
            <ButtonContainer>
              <Button
                disabled={!isFeatureEnabled}
                appearance="primary"
                caption="Duplicate email"
                progressIndicator={duplicatingBrief}
                progressCaption="Duplicating email"
                onClick={() => {
                  this._onDuplicateBrief()

                  mixpanel.sendPureEvent(
                    'Email Creator - Duplicate existing email',
                    {
                      Context: 'Stats',
                    },
                  )
                }}
              />
              <Button
                caption="View summary of content"
                link={`/campaigns/${params.campaignId}/stats/summary`}
              />
            </ButtonContainer>
          }
        >
          <div className={styles.campaignStatsSubheader}>
            <div className={styles.campaignStatsSubheaderRecipients}>
              {campaign.recipients_count}{' '}
              {campaign.recipients_count === 1 ? (
                <span>recipient</span>
              ) : (
                <span>recipients</span>
              )}
            </div>
            <div className={styles.campaignStatsSubheaderSubject}>
              Subject: {get(campaign, 'template_data.subject', '')}
            </div>
          </div>
        </PageHeader>
        <CampaignDashboard campaignId={params.campaignId} />
        {children}
      </div>
    )
  }
}

const ConnectedCampaignStatsPage = connector(CampaignStatsPage)

function CampaignStatsPageWithRouter(props: { children: React.ReactNode }) {
  const params = useParams<{ campaignId: string }>()
  return (
    <ConnectedCampaignStatsPage
      {...props}
      params={params as { campaignId: string }}
    />
  )
}

export default CampaignStatsPageWithRouter
